import { EventType } from "@/analytics/analytics-events";
import { PanoObject } from "@/object-cache";
import {
  useOverrideCursor,
  useThreeEventTarget,
} from "@faro-lotv/app-component-toolbox";
import { Analytics } from "@faro-lotv/foreign-observers";
import { generateGUID } from "@faro-lotv/foundation";
import { useCallback, type JSX } from "react";
import { Mesh } from "three";
import { CreateManualBlurPreview } from "./create-manual-blur-preview";
import { ManualBlurPreview } from "./manual-blur-preview";
import { useManualBlurringStore } from "./manual-blurring-store";
import { useDetectManualBlurringMount } from "./use-detect-manual-blur-mounts";

type ManualBlurringToolProps = {
  isActive: boolean;
  activePano: PanoObject;
};

/** @returns The 3d components needed to implement the controls and preview for the manual blurring. */
export function ManualBlurringTool({
  isActive,
  activePano,
}: ManualBlurringToolProps): JSX.Element {
  const { manualBlurs, currentlySavingManualBlurs, addManualBlur } =
    useManualBlurringStore();

  const domElement = useThreeEventTarget();
  useOverrideCursor("crosshair", isActive, domElement);

  useDetectManualBlurringMount(isActive);

  const onBlurCreated = useCallback(
    (mesh: Mesh) => {
      Analytics.track(EventType.drawManualBlur);

      addManualBlur(activePano.iElement.id, {
        id: generateGUID(),
        position: mesh.position.clone(),
        quaternion: mesh.quaternion.clone(),
        scale: mesh.scale.clone(),
      });
    },
    [activePano.iElement.id, addManualBlur],
  );

  return (
    <>
      {isActive && <CreateManualBlurPreview onCreated={onBlurCreated} />}

      {/** Shows previews for the blurs currently being edited */}
      {manualBlurs[activePano.iElement.id]?.map((blur) => (
        <ManualBlurPreview key={blur.id} manualBlur={blur} />
      ))}

      {/** Shows previews for the blurs currently being saved in the background */}
      {currentlySavingManualBlurs[activePano.iElement.id]?.map((blur) => (
        <ManualBlurPreview key={blur.id} manualBlur={blur} />
      ))}
    </>
  );
}
